import type { Profile } from '../../../types';

/**
 * Parsing das menções de um comentário — separado do componente de propósito.
 *
 * Vive num .ts (e não dentro do ComentarioTexto.tsx) porque o type-stripping nativo do Node não
 * lê JSX: função pura em arquivo .tsx é função pura sem teste. Aqui ela roda no `node --test`.
 */

export interface TarefaMencionavel {
  id: number | string;
  titulo: string;
}

export interface ParteMencao {
  texto: string;
  /** Id (ou nome, no fallback) do usuário mencionado com "@". */
  mencaoDe?: string;
  /** Id da tarefa mencionada com "#". */
  mencaoDeTarefa?: string;
}

/**
 * Quebra o texto em pedaços, marcando as menções de usuário (`@`) e de tarefa (`#`).
 *
 * Nome e título têm espaço, então não dá pra usar `@\w+`: o casamento é contra a lista de nomes
 * conhecidos, do mais longo pro mais curto (senão "@Victor" ganharia de "@Victor Paulo", e
 * "#Ingestão" ganharia de "#Ingestão WhatsApp").
 */
export const partesComMencoes = (
    texto: string,
    usuarios: Pick<Profile, 'id' | 'full_name'>[] = [],
    tarefas: TarefaMencionavel[] = [],
): ParteMencao[] => {
    const nomes = usuarios
        .map(u => ({ id: u.id, nome: (u.full_name || '').trim() }))
        .filter(u => u.nome.length > 0)
        .sort((a, b) => b.nome.length - a.nome.length);
    const titulos = tarefas
        .map(t => ({ id: String(t.id), nome: (t.titulo || '').trim() }))
        .filter(t => t.nome.length > 0)
        .sort((a, b) => b.nome.length - a.nome.length);

    const partes: ParteMencao[] = [];
    let restante = texto;
    let acumulado = '';

    const empurrarAcumulado = () => {
        if (acumulado) partes.push({ texto: acumulado });
        acumulado = '';
    };

    while (restante.length > 0) {
        const posArroba = restante.indexOf('@');
        const posCerquilha = titulos.length > 0 ? restante.indexOf('#') : -1;
        const candidatos = [posArroba, posCerquilha].filter(p => p !== -1);
        if (candidatos.length === 0) { acumulado += restante; break; }

        const pos = Math.min(...candidatos);
        const gatilho = restante[pos];
        acumulado += restante.slice(0, pos);
        const depois = restante.slice(pos + 1);
        const lista = gatilho === '@' ? nomes : titulos;

        const casado = lista.find(x => depois.toLowerCase().startsWith(x.nome.toLowerCase()));
        if (casado) {
            empurrarAcumulado();
            const rotulo = `${gatilho}${depois.slice(0, casado.nome.length)}`;
            partes.push(gatilho === '@'
                ? { texto: rotulo, mencaoDe: casado.id }
                : { texto: rotulo, mencaoDeTarefa: casado.id });
            restante = depois.slice(casado.nome.length);
            continue;
        }

        // Fallback só para usuário: "@Nome Sobrenome" quando a lista não está disponível.
        // Não existe equivalente para tarefa — "#1" costuma ser numeração de texto, não menção.
        if (gatilho === '@') {
            const solto = depois.match(/^[\p{Lu}][\p{L}]+(?: [\p{Lu}][\p{L}]+)*/u);
            if (solto) {
                empurrarAcumulado();
                partes.push({ texto: `@${solto[0]}`, mencaoDe: solto[0] });
                restante = depois.slice(solto[0].length);
                continue;
            }
        }

        acumulado += gatilho;
        restante = depois;
    }
    empurrarAcumulado();
    return partes;
};
